import {Typography, Button, Box, Dialog, IconButton} from "@mui/material";
import {Container} from "@mui/system";
import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import MySnackbar from "../components/MySnackbar";
import useSnackbar from "../hooks/useSnackbar";
import {fetchReviewRequestById, processReviewRequest} from "../http/reviewRequestsAPI";
import {getImageUrl} from "../helpers/getImageUrl";
import CloseIcon from "@mui/icons-material/Close";

const reviewLinkTypeLabels = {
  review1: "Ссылка на отзыв №1",
  review2: "Ссылка на отзыв №2",
  review3: "Ссылка на отзыв №3",
  review4: "Ссылка на отзыв №4",
  review5: "Ссылка на отзыв №5"
};

const ReviewRequestPage = () => {
    const params = useParams();
    const navigate = useNavigate();

    const [reviewRequest, setReviewRequest] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [isImageOpen, setIsImageOpen] = useState(false);

    const snack = useSnackbar(false, "Ошибка");

    const loadReviewRequest = () => {
        fetchReviewRequestById(params.id).then((data) => {
            setReviewRequest(data);
        });
    };

    useEffect(() => {
        loadReviewRequest();
    }, [params.id]);

    const handleDecision = async (selected) => {
        setIsProcessing(true);
        try {
            await processReviewRequest(reviewRequest.id, selected);
            loadReviewRequest();
        } catch (error) {
            console.error("Ошибка при обработке заявки:", error);
            snack.handleOpen("Ошибка при обработке заявки");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Container
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                py: 3
            }}
        >
            <MySnackbar {...snack} />

            <Button
                variant="text"
                onClick={() => navigate(-1)}
                sx={{alignSelf: "flex-start", borderRadius: 2}}
            >
                ← Назад
            </Button>

            <Typography variant="h5" sx={{fontWeight: 700}}>
                Заявка на проверку отзыва
            </Typography>

            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: {xs: "column", md: "row"},
                    alignItems: "flex-start",
                    gap: 3
                }}
            >
                <Box sx={{width: {xs: "100%", md: 360}, flexShrink: 0}}>
                    {reviewRequest?.image && (
                        <Box
                            component="img"
                            src={getImageUrl(reviewRequest.image)}
                            alt="Фото отзыва"
                            onClick={() => setIsImageOpen(true)}
                            sx={{
                                width: "100%",
                                maxWidth: 360,
                                maxHeight: {xs: "52vh", md: "66vh"},
                                objectFit: "contain",
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                backgroundColor: "#fafafa",
                                cursor: "zoom-in"
                            }}
                        />
                    )}
                </Box>

                <Box sx={{display: "flex", flexDirection: "column", gap: 1.5, flex: 1, minWidth: 0}}>
                    <Typography variant="h6" sx={{fontWeight: "bold"}}>
                        {reviewRequest.decision === null
                            ? "Решение еще не принято"
                            : reviewRequest.decision === true
                                ? "Баллы начислены"
                                : "Отказано в начислении"}
                    </Typography>

                    <Typography>
                        {`Пациент: ${reviewRequest.user?.name}, телефон: ${reviewRequest.user?.phone}`}
                    </Typography>

                    <Typography>
                        Дата запроса:{" "}
                        {reviewRequest.createdAt ? new Date(reviewRequest.createdAt).toLocaleDateString() : "—"}
                    </Typography>

                    <Typography>
                        Тип отзыва: {reviewRequest.reviewTargetType === "doctor" ? "На врача" : "На филиал"}
                    </Typography>

                    {reviewRequest.reviewTargetType === "branch" && reviewRequest.branch && (
                        <Typography>
                            Филиал: {reviewRequest.branch.name}
                        </Typography>
                    )}

                    {reviewRequest.reviewTargetType === "doctor" && reviewRequest.doctor && (
                        <Typography>
                            Врач: {reviewRequest.doctor.name}
                        </Typography>
                    )}

                    {reviewRequest.reviewLinkType && (
                        <Typography>
                            Площадка для отзыва: {reviewLinkTypeLabels[reviewRequest.reviewLinkType] || reviewRequest.reviewLinkType}
                        </Typography>
                    )}

                    {reviewRequest.reviewLinkValue && (
                        <Typography>
                            Ссылка на отзыв:{" "}
                            <a href={reviewRequest.reviewLinkValue} target="_blank" rel="noopener noreferrer">
                                {reviewRequest.reviewLinkValue}
                            </a>
                        </Typography>
                    )}

                    {reviewRequest.admin && (
                        <Typography>
                            Менеджер: {reviewRequest.admin.internalName || reviewRequest.admin.name}, телефон: {reviewRequest.admin.phone}
                        </Typography>
                    )}

                    {reviewRequest.adminComment && (
                        <Typography>
                            Комментарий: {reviewRequest.adminComment}
                        </Typography>
                    )}

                    {reviewRequest.decision === null && (
                        <Box sx={{display: "flex", gap: 2, mt: 1}}>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => handleDecision("give")}
                                disabled={isProcessing}
                            >
                                Одобрить
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleDecision("refuse")}
                                disabled={isProcessing}
                            >
                                Отклонить
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            <Dialog
                fullScreen
                open={isImageOpen}
                onClose={() => setIsImageOpen(false)}
            >
                <Box sx={{position: "relative", width: "100%", height: "100%", backgroundColor: "#111"}}>
                    <IconButton
                        onClick={() => setIsImageOpen(false)}
                        sx={{position: "absolute", top: 12, right: 12, color: "#fff", zIndex: 1}}
                    >
                        <CloseIcon/>
                    </IconButton>
                    {reviewRequest?.image && (
                        <Box
                            component="img"
                            src={getImageUrl(reviewRequest.image)}
                            alt="Фото отзыва"
                            sx={{width: "100%", height: "100%", objectFit: "contain"}}
                        />
                    )}
                </Box>
            </Dialog>
        </Container>
    );
};

export default ReviewRequestPage;
